/**
 * FP3-029C — Coverage & Reassignment panel.
 *
 * Embedded on Admin Provider Detail beneath Active Students. Lets an
 * admin pick one or more students from a provider's caseload and:
 *   - Set temporary coverage (date range + covering provider)
 *   - Permanently reassign (new primary provider)
 *
 * Cross-discipline targets emit a calm soft warning surfaced via
 * `needs_force=true` on the first POST; the modal then asks the
 * admin to confirm with a second click (sends `force=true`).
 *
 * Existing assistant routing (FP3-029B6C) is NOT touched — that
 * affordance still lives on each student row and uses the
 * `POST /students/{id}/route` endpoint. Coverage / reassignment use
 * the new `/api/admin/students/coverage` and
 * `/api/admin/students/reassign` endpoints.
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Users,
  ArrowsLeftRight,
  CalendarBlank,
  X,
  Warning,
  Clock,
  CaretDown,
  CaretUp,
} from "@phosphor-icons/react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import api from "@/lib/api";

const todayIso = () => new Date().toISOString().slice(0, 10);

const addDaysIso = (n) => {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};

export default function CoveragePanel({ provider, allProviders, onChanged }) {
  const [open, setOpen] = useState(true);
  const [selected, setSelected] = useState(() => new Set());
  const [coverageOpen, setCoverageOpen] = useState(false);
  const [reassignOpen, setReassignOpen] = useState(false);
  const [history, setHistory] = useState({ outgoing: [], incoming: [] });
  const [historyLoading, setHistoryLoading] = useState(false);

  const students = useMemo(
    () => provider?.active_students || [],
    [provider]
  );

  const loadHistory = async () => {
    if (!provider?.id) return;
    setHistoryLoading(true);
    try {
      const [out, inc] = await Promise.all([
        api
          .getProviderCoverage(provider.id, "outgoing")
          .catch(() => ({ data: { items: [] } })),
        api
          .getProviderCoverage(provider.id, "incoming")
          .catch(() => ({ data: { items: [] } })),
      ]);
      setHistory({
        outgoing: out.data?.items || [],
        incoming: inc.data?.items || [],
      });
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provider?.id]);

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAll = () => setSelected(new Set(students.map((s) => s.id)));
  const selectNone = () => setSelected(new Set());

  const refreshAfter = async () => {
    await loadHistory();
    onChanged?.();
    setSelected(new Set());
  };

  const activeOutgoing = (history.outgoing || []).filter(
    (r) => r.is_currently_active
  );
  const activeIncoming = (history.incoming || []).filter(
    (r) => r.is_currently_active
  );

  return (
    <Card
      data-testid="coverage-panel"
      className="border-[#E5E3DB] rounded-2xl mb-4"
    >
      <CardContent className="p-5">
        {/* Header */}
        <button
          data-testid="coverage-panel-toggle"
          onClick={() => setOpen((o) => !o)}
          className="w-full flex items-center justify-between gap-2 text-left"
        >
          <h3 className="text-[13.5px] font-semibold text-[#1F2937] heading-font flex items-center gap-1.5">
            <ArrowsLeftRight
              size={14}
              weight="duotone"
              className="text-[#3F4756]"
            />
            Coverage & Reassignment
            {(activeOutgoing.length > 0 || activeIncoming.length > 0) && (
              <span className="ml-1.5 text-[11px] font-medium text-[#1E3F33] bg-[#E8F0EB] rounded-full px-2 py-0.5 tabular-nums">
                {activeOutgoing.length + activeIncoming.length} active
              </span>
            )}
          </h3>
          {open ? (
            <CaretUp size={14} className="text-[#6B7280]" />
          ) : (
            <CaretDown size={14} className="text-[#6B7280]" />
          )}
        </button>

        {open && (
          <div className="mt-3 pt-3 border-t border-[#F1F2F4] space-y-4">
            {/* Active coverage chips */}
            {(activeOutgoing.length > 0 || activeIncoming.length > 0) && (
              <div className="space-y-1.5">
                {activeOutgoing.map((r) => (
                  <CoverageChip
                    key={r.id}
                    row={r}
                    direction="outgoing"
                    onEnded={refreshAfter}
                  />
                ))}
                {activeIncoming.map((r) => (
                  <CoverageChip
                    key={r.id}
                    row={r}
                    direction="incoming"
                    onEnded={refreshAfter}
                  />
                ))}
              </div>
            )}

            {/* Bulk select + actions */}
            {students.length === 0 ? (
              <p className="text-[12px] text-[#6B7280]">
                No active students yet — nothing to cover or reassign.
              </p>
            ) : (
              <>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-2 text-[11px] text-[#6B7280]">
                    <span>{selected.size} selected</span>
                    <button
                      data-testid="coverage-select-all"
                      type="button"
                      onClick={selectAll}
                      className="text-[#3F4756] hover:text-[#1F2937] underline-offset-2 hover:underline"
                    >
                      Select all
                    </button>
                    <span className="text-[#D6D3CB]">·</span>
                    <button
                      data-testid="coverage-select-none"
                      type="button"
                      onClick={selectNone}
                      className="text-[#3F4756] hover:text-[#1F2937] underline-offset-2 hover:underline"
                    >
                      None
                    </button>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      data-testid="coverage-open-coverage-modal"
                      size="sm"
                      variant="outline"
                      disabled={selected.size === 0}
                      onClick={() => setCoverageOpen(true)}
                      className="h-8 text-[12px] border-[#E5E3DB] text-[#1E3F33] hover:bg-[#E8F0EB] disabled:opacity-50"
                    >
                      <CalendarBlank size={12} className="mr-1" />
                      Set temporary coverage
                    </Button>
                    <Button
                      data-testid="coverage-open-reassign-modal"
                      size="sm"
                      disabled={selected.size === 0}
                      onClick={() => setReassignOpen(true)}
                      className="h-8 text-[12px] bg-[#1E3F33] hover:bg-[#173127] disabled:bg-[#1E3F33]/60 text-white"
                    >
                      <ArrowsLeftRight size={12} className="mr-1" />
                      Reassign students
                    </Button>
                  </div>
                </div>

                {/* Student multi-select rows */}
                <ul
                  data-testid="coverage-student-list"
                  className="divide-y divide-[#F1F2F4] border border-[#F1F2F4] rounded-lg"
                >
                  {students.map((s) => (
                    <li
                      key={s.id}
                      className="flex items-center gap-3 px-3 py-2 hover:bg-[#FBFAF6]"
                    >
                      <input
                        type="checkbox"
                        data-testid={`coverage-select-${s.id}`}
                        checked={selected.has(s.id)}
                        onChange={() => toggleSelect(s.id)}
                        className="rounded border-[#E5E3DB]"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-[12.5px] text-[#1F2937]">
                          {s.first_name} {s.last_name}
                        </div>
                        <div className="text-[10.5px] text-[#9AA0A6] truncate">
                          {s.readable_id ? `${s.readable_id} · ` : ""}
                          {s.grade ? `Grade ${s.grade}` : ""}
                          {s.school_name ? ` · ${s.school_name}` : ""}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
                <p className="text-[10.5px] text-[#9AA0A6] leading-snug">
                  Temporary coverage keeps the original primary attached
                  and grants the covering provider access for the date
                  window only. Reassignment moves future ownership
                  permanently; past sessions remain attributed to the
                  provider who logged them.
                </p>
              </>
            )}

            {/* Recent history (collapsed below active) */}
            <CoverageHistoryList
              outgoing={history.outgoing}
              incoming={history.incoming}
              loading={historyLoading}
            />
          </div>
        )}
      </CardContent>

      {coverageOpen && (
        <CoverageModal
          provider={provider}
          studentIds={[...selected]}
          students={students}
          allProviders={allProviders}
          onClose={() => setCoverageOpen(false)}
          onCreated={async () => {
            setCoverageOpen(false);
            await refreshAfter();
          }}
        />
      )}
      {reassignOpen && (
        <ReassignModal
          provider={provider}
          studentIds={[...selected]}
          students={students}
          allProviders={allProviders}
          onClose={() => setReassignOpen(false)}
          onReassigned={async () => {
            setReassignOpen(false);
            await refreshAfter();
          }}
        />
      )}
    </Card>
  );
}

// ── Chip surfacing an active coverage row ────────────────────────────

function CoverageChip({ row, direction, onEnded }) {
  const [busy, setBusy] = useState(false);
  const end = async () => {
    setBusy(true);
    try {
      await api.endCoverage(row.id);
      toast.success("Coverage ended");
      onEnded?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not end coverage");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div
      data-testid={`coverage-active-chip-${row.id}`}
      className="flex items-center justify-between gap-2 bg-[#FBFAF6] border border-[#E5E3DB] rounded-lg px-3 py-1.5"
    >
      <div className="min-w-0">
        <div className="text-[11.5px] text-[#1F2937] truncate">
          <Clock
            size={11}
            weight="duotone"
            className="inline mr-1 text-[#3F4756]"
          />
          {direction === "outgoing"
            ? "Covered by another provider"
            : "Covering for another provider"}
          {row.student_name ? ` · ${row.student_name}` : ""}
        </div>
        <div className="text-[10.5px] text-[#6B7280]">
          {row.starts_on} → {row.ends_on}
          {row.reason ? ` · ${row.reason}` : ""}
        </div>
      </div>
      <Button
        data-testid={`coverage-end-${row.id}`}
        size="sm"
        variant="outline"
        onClick={end}
        disabled={busy}
        className="h-7 text-[11px] border-[#E5E3DB] text-[#7A3A2C] hover:bg-[#F8E8E2]"
      >
        End
      </Button>
    </div>
  );
}

// ── Past history rollup ──────────────────────────────────────────────

function CoverageHistoryList({ outgoing, incoming, loading }) {
  const ended = [
    ...(outgoing || []),
    ...(incoming || []),
  ].filter((r) => (r.status || "active") !== "active");
  if (loading) {
    return (
      <p className="text-[11px] text-[#9AA0A6]">Loading coverage history…</p>
    );
  }
  if (ended.length === 0) return null;
  return (
    <details
      data-testid="coverage-history"
      className="text-[11.5px] text-[#3F4756]"
    >
      <summary className="cursor-pointer text-[#6B7280] hover:text-[#1F2937] mb-1">
        Coverage history ({ended.length})
      </summary>
      <ul className="space-y-1 pl-2 mt-2">
        {ended.slice(0, 20).map((r) => (
          <li key={r.id} className="text-[10.5px] text-[#6B7280]">
            <span className="text-[#1F2937]">
              {r.student_name || r.student_id}
            </span>{" "}
            · {r.starts_on} → {r.ends_on} · {r.status}
          </li>
        ))}
      </ul>
    </details>
  );
}

// ── Coverage modal ───────────────────────────────────────────────────

function CoverageModal({
  provider,
  studentIds,
  students,
  allProviders,
  onClose,
  onCreated,
}) {
  const [coveringProviderId, setCoveringProviderId] = useState("");
  const [startsOn, setStartsOn] = useState(todayIso());
  const [endsOn, setEndsOn] = useState(addDaysIso(14));
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [warning, setWarning] = useState("");
  const [needsForce, setNeedsForce] = useState(false);

  const eligible = useMemo(
    () =>
      (allProviders || []).filter(
        (p) =>
          p.id !== provider.id &&
          (p.provider_role_type || "primary") === "primary" &&
          (p.account_status || "active") !== "deactivated" &&
          (p.provider_status || "available") !== "inactive"
      ),
    [allProviders, provider.id]
  );

  const submit = async (force = false) => {
    setSubmitting(true);
    setWarning("");
    try {
      const res = await api.createCoverage({
        student_ids: studentIds,
        covering_provider_id: coveringProviderId,
        starts_on: startsOn,
        ends_on: endsOn,
        reason,
        force,
      });
      const body = res.data || {};
      if (body.needs_force) {
        setWarning(body.discipline_warning || "Cross-discipline pairing.");
        setNeedsForce(true);
        return;
      }
      toast.success(
        `Coverage set for ${studentIds.length} student${studentIds.length === 1 ? "" : "s"}`
      );
      onCreated?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        (typeof detail === "string" && detail) ||
          "Could not set coverage"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const dateInvalid = startsOn > endsOn;
  const canSubmit = coveringProviderId && !dateInvalid;

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent
        data-testid="coverage-modal"
        className="max-w-md max-h-[90vh] overflow-y-auto p-0 border-[#E5E3DB] rounded-2xl"
      >
        <header className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-[#F1F2F4]">
          <h3 className="text-[15px] font-semibold text-[#1F2937] heading-font flex items-center gap-2">
            <CalendarBlank
              size={16}
              weight="duotone"
              className="text-[#1E3F33]"
            />
            Set temporary coverage
          </h3>
          <button
            type="button"
            data-testid="coverage-modal-close"
            onClick={onClose}
            className="text-[#6B7280] hover:text-[#1F2937] p-1"
          >
            <X size={14} />
          </button>
        </header>

        <div className="px-5 py-4 space-y-3.5">
          <div className="text-[11.5px] text-[#3F4756] bg-[#FBFAF6] border border-[#F1F2F4] rounded-md px-3 py-2">
            <Users
              size={12}
              weight="duotone"
              className="inline mr-1 text-[#3F4756]"
            />
            {studentIds.length} student{studentIds.length === 1 ? "" : "s"}{" "}
            selected from {provider.first_name} {provider.last_name}
            {studentIds.length <= 3 && students.length > 0 && (
              <span className="text-[#6B7280]">
                {" — "}
                {studentIds
                  .map((id) => {
                    const s = students.find((x) => x.id === id);
                    return s ? `${s.first_name} ${s.last_name}` : id;
                  })
                  .join(", ")}
              </span>
            )}
          </div>

          <Field label="Covering provider" required>
            <select
              data-testid="coverage-covering-provider"
              value={coveringProviderId}
              onChange={(e) => {
                setCoveringProviderId(e.target.value);
                setNeedsForce(false);
                setWarning("");
              }}
              className={inputClasses}
            >
              <option value="">— Select provider —</option>
              {eligible.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.first_name} {p.last_name}
                  {p.role ? ` · ${p.role}` : ""}
                </option>
              ))}
            </select>
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Coverage starts" required>
              <input
                data-testid="coverage-starts-on"
                type="date"
                value={startsOn}
                onChange={(e) => setStartsOn(e.target.value)}
                className={inputClasses}
              />
            </Field>
            <Field label="Coverage ends" required>
              <input
                data-testid="coverage-ends-on"
                type="date"
                value={endsOn}
                onChange={(e) => setEndsOn(e.target.value)}
                min={startsOn}
                className={inputClasses}
              />
            </Field>
          </div>
          {dateInvalid && (
            <p className="text-[10.5px] text-[#7A3A2C] flex items-center gap-1">
              <Warning size={10} weight="duotone" />
              End date must be on or after start date.
            </p>
          )}

          <Field label="Reason / note">
            <textarea
              data-testid="coverage-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              maxLength={500}
              placeholder="e.g. Maternity leave through end of semester"
              className={`${inputClasses} resize-none`}
            />
          </Field>

          {warning && (
            <div
              data-testid="coverage-warning"
              className="text-[11.5px] text-[#7A3A2C] bg-[#F8E8E2] border border-[#E8C7BC] rounded-md px-3 py-2 flex items-start gap-2"
            >
              <Warning
                size={13}
                weight="duotone"
                className="mt-0.5 shrink-0"
              />
              <div>
                <div className="font-semibold mb-0.5">
                  Cross-discipline pairing
                </div>
                {warning}
              </div>
            </div>
          )}
        </div>

        <footer className="flex justify-end gap-2 px-5 py-3 border-t border-[#F1F2F4] bg-[#FBFAF6] rounded-b-2xl">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onClose}
            className="h-8 text-[12px] border-[#E5E3DB] text-[#6B7280]"
          >
            Cancel
          </Button>
          <Button
            type="button"
            data-testid="coverage-submit"
            size="sm"
            disabled={!canSubmit || submitting}
            onClick={() => submit(needsForce)}
            className="h-8 text-[12px] bg-[#1E3F33] hover:bg-[#173127] disabled:bg-[#1E3F33]/60 text-white"
          >
            {submitting
              ? "Setting…"
              : needsForce
                ? "Confirm coverage"
                : "Set coverage"}
          </Button>
        </footer>
      </DialogContent>
    </Dialog>
  );
}

// ── Reassign modal ───────────────────────────────────────────────────

function ReassignModal({
  provider,
  studentIds,
  students,
  allProviders,
  onClose,
  onReassigned,
}) {
  const [toProviderId, setToProviderId] = useState("");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [warning, setWarning] = useState("");
  const [needsForce, setNeedsForce] = useState(false);

  const eligible = useMemo(
    () =>
      (allProviders || []).filter(
        (p) =>
          p.id !== provider.id &&
          (p.provider_role_type || "primary") === "primary" &&
          (p.account_status || "active") !== "deactivated" &&
          (p.provider_status || "available") !== "inactive"
      ),
    [allProviders, provider.id]
  );

  const submit = async (force = false) => {
    setSubmitting(true);
    setWarning("");
    try {
      const res = await api.reassignStudents({
        student_ids: studentIds,
        from_provider_id: provider.id,
        to_provider_id: toProviderId,
        reason,
        force,
      });
      const body = res.data || {};
      if (body.needs_force) {
        setWarning(body.discipline_warning || "Cross-discipline reassignment.");
        setNeedsForce(true);
        return;
      }
      toast.success(
        `Reassigned ${studentIds.length} student${studentIds.length === 1 ? "" : "s"}`
      );
      onReassigned?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        (typeof detail === "string" && detail) ||
          "Could not reassign students"
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent
        data-testid="reassign-modal"
        className="max-w-md max-h-[90vh] overflow-y-auto p-0 border-[#E5E3DB] rounded-2xl"
      >
        <header className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-[#F1F2F4]">
          <h3 className="text-[15px] font-semibold text-[#1F2937] heading-font flex items-center gap-2">
            <ArrowsLeftRight
              size={16}
              weight="duotone"
              className="text-[#1E3F33]"
            />
            Reassign students
          </h3>
          <button
            type="button"
            data-testid="reassign-modal-close"
            onClick={onClose}
            className="text-[#6B7280] hover:text-[#1F2937] p-1"
          >
            <X size={14} />
          </button>
        </header>

        <div className="px-5 py-4 space-y-3.5">
          <div className="text-[11.5px] text-[#3F4756] bg-[#FBFAF6] border border-[#F1F2F4] rounded-md px-3 py-2">
            Moving {studentIds.length} student
            {studentIds.length === 1 ? "" : "s"} from{" "}
            <span className="font-semibold">
              {provider.first_name} {provider.last_name}
            </span>
            's caseload. Past sessions remain attributed to{" "}
            {provider.first_name}.
          </div>

          <Field label="New primary provider" required>
            <select
              data-testid="reassign-to-provider"
              value={toProviderId}
              onChange={(e) => {
                setToProviderId(e.target.value);
                setNeedsForce(false);
                setWarning("");
              }}
              className={inputClasses}
            >
              <option value="">— Select provider —</option>
              {eligible.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.first_name} {p.last_name}
                  {p.role ? ` · ${p.role}` : ""}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Reason / note">
            <textarea
              data-testid="reassign-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              maxLength={500}
              placeholder="e.g. Caseload rebalancing for spring semester"
              className={`${inputClasses} resize-none`}
            />
          </Field>

          {warning && (
            <div
              data-testid="reassign-warning"
              className="text-[11.5px] text-[#7A3A2C] bg-[#F8E8E2] border border-[#E8C7BC] rounded-md px-3 py-2 flex items-start gap-2"
            >
              <Warning
                size={13}
                weight="duotone"
                className="mt-0.5 shrink-0"
              />
              <div>
                <div className="font-semibold mb-0.5">
                  Cross-discipline reassignment
                </div>
                {warning}
              </div>
            </div>
          )}
        </div>

        <footer className="flex justify-end gap-2 px-5 py-3 border-t border-[#F1F2F4] bg-[#FBFAF6] rounded-b-2xl">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onClose}
            className="h-8 text-[12px] border-[#E5E3DB] text-[#6B7280]"
          >
            Cancel
          </Button>
          <Button
            type="button"
            data-testid="reassign-submit"
            size="sm"
            disabled={!toProviderId || submitting}
            onClick={() => submit(needsForce)}
            className="h-8 text-[12px] bg-[#1E3F33] hover:bg-[#173127] disabled:bg-[#1E3F33]/60 text-white"
          >
            {submitting
              ? "Reassigning…"
              : needsForce
                ? "Confirm reassignment"
                : "Reassign students"}
          </Button>
        </footer>
      </DialogContent>
    </Dialog>
  );
}

// ── shared mini-controls ────────────────────────────────────────────

const inputClasses =
  "w-full text-[12.5px] border border-[#E5E3DB] rounded-md p-2 bg-white focus:outline-none focus:border-[#1E3F33]";

function Field({ label, required, hint, children }) {
  return (
    <label className="block">
      <span className="text-[10.5px] uppercase tracking-[0.08em] text-[#6B7280] font-semibold flex items-center gap-1">
        {label}
        {required && <span className="text-[#7A3A2C]">*</span>}
      </span>
      <div className="mt-1">{children}</div>
      {hint && (
        <span className="text-[10.5px] text-[#9AA0A6] mt-1 block">{hint}</span>
      )}
    </label>
  );
}
